import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import { FAQ } from '@/types/knowledge'

// Debug flag to help troubleshoot data loading issues
const DEBUG_KNOWLEDGE = true

interface KnowledgeState {
  faqs: FAQ[]
  isLoading: boolean
  loadFAQs: () => Promise<void>
  searchFAQs: (query: string) => Promise<void>
  createFAQ: (data: Partial<FAQ>) => Promise<void>
  updateFAQ: (id: string, data: Partial<FAQ>) => Promise<void>
  deleteFAQ: (id: string) => Promise<void>
}

export const useKnowledgeStore = create<KnowledgeState>((set, get) => ({
  faqs: [],
  isLoading: false,

  loadFAQs: async () => {
    if (DEBUG_KNOWLEDGE) console.log('[KNOWLEDGE] loadFAQs called')
    set({ isLoading: true })

    try {
      if (DEBUG_KNOWLEDGE) console.log('[KNOWLEDGE] Fetching from Supabase...')

      const { data, error } = await supabase
        .from('knowledge')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('[KNOWLEDGE] Error loading FAQs:', error)
        console.error('[KNOWLEDGE] Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        })
        set({ isLoading: false, faqs: [] })
        return
      }

      console.log(`[KNOWLEDGE] ✓ Loaded ${data?.length || 0} FAQs successfully`)
      set({ faqs: data || [], isLoading: false })
    } catch (err) {
      console.error('[KNOWLEDGE] Unexpected error loading FAQs:', err)
      set({ isLoading: false, faqs: [] })
    }
  },

  searchFAQs: async (query: string) => {
    set({ isLoading: true })

    try {
      // Use ilike for case-insensitive text search
      // Search in both question and answer fields
      const { data, error } = await supabase
        .from('knowledge')
        .select('*')
        .or(`question.ilike.%${query}%,answer.ilike.%${query}%`)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error searching FAQs:', error)
        console.error('Search error details:', error.message, error.code, error.details)
        set({ isLoading: false, faqs: [] })
        return
      }

      console.log(`[KNOWLEDGE] Search returned ${data?.length || 0} results`)
      set({ faqs: data || [], isLoading: false })
    } catch (err) {
      console.error('[KNOWLEDGE] Unexpected error searching FAQs:', err)
      set({ isLoading: false, faqs: [] })
    }
  },

  createFAQ: async (data: Partial<FAQ>) => {
    const { error } = await supabase
      .from('knowledge')
      .insert([data as any])

    if (error) {
      console.error('Error creating FAQ:', error)
      throw error
    }

    await get().loadFAQs()
  },

  updateFAQ: async (id: string, data: Partial<FAQ>) => {
    const { error } = await supabase
      .from('knowledge')
      .update(data as any)
      .eq('id', id)

    if (error) {
      console.error('Error updating FAQ:', error)
      throw error
    }

    // If archiving knowledge, also set import_sources to ignored
    if (data.status === 'archived') {
      // Get source_uid for this knowledge entry
      const { data: knowledgeData, error: fetchError } = await supabase
        .from('knowledge')
        .select('source_uid')
        .eq('id', id)
        .single()

      if (!fetchError && knowledgeData?.source_uid) {
        const { error: sourceError } = await supabase
          .from('import_sources')
          .update({
            status: 'ignored',
            updated_at: new Date().toISOString()
          })
          .eq('uid', knowledgeData.source_uid)

        if (sourceError) {
          console.error('Error updating import_sources:', sourceError)
        }
      }
    }

    await get().loadFAQs()
  },

  deleteFAQ: async (id: string) => {
    const { error } = await supabase
      .from('knowledge')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting FAQ:', error)
      throw error
    }

    await get().loadFAQs()
  }
}))